import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { safeNextPath } from '../../utils/safeNextPath';
import axios from 'axios';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const inputStyle = {
  width: '100%',
  height: 48,
  padding: '0 14px',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  fontSize: 14,
  color: '#111827',
  backgroundColor: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

export default function DoorlyProLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerLogin } = useDoorlyProAuth();

  // ── 🚨 WHERE THEY WERE ACTUALLY GOING ─────────────────────────────────────
  //
  // Every CTA in the partner activation sequence points at an authenticated
  // page. Before this, an unauthenticated click rendered an empty page (see
  // DoorlyProOnboarding) — and sending them to login without carrying the
  // destination would have replaced one dead end with another: they would sign
  // in and arrive at the dashboard, having lost the thing the email asked them
  // to open.
  //
  // ⚠️ THE GUARD LIVES IN utils/safeNextPath.js, not inline here. It was an IIFE
  // in this file, which is the shape that collects a comment asserting it is safe
  // and no test proving it. Its header explains every rejection.
  const nextPath = safeNextPath(location.search);
  const landing = nextPath || '/doorlypro/dashboard';
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/doorlypro/login`, {
        email: form.email.trim(),
        password: form.password,
      });
      partnerLogin(res.data.token);
      navigate(landing);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/doorlypro/login" className="inline-block">
            <img
              src={`${process.env.PUBLIC_URL}/assets/DoorlyPro.png`}
              alt="DoorlyPro"
              style={{ height: 40, width: 'auto' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'inline';
              }}
            />
            <span style={{ display: 'none', fontSize: 24, fontWeight: 800, color: '#3a6ad9' }}>
              Doorly<span style={{ color: '#111827' }}>Pro</span>
            </span>
          </Link>
          <h1 className="text-2xl font-bold text-black mt-6 mb-1">Partner Sign In</h1>
          <p className="text-gray-500 text-sm">Sign in to your DoorlyPro partner account.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          {error && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}
          {/* 🔑 THE NOTICE A PAGE SENDS HERE (hand-test pages E2E, 2026-11-02): the reset page navigates here with
              state.message "Password reset successfully. Please sign in." and nothing rendered it, so a partner
              who had just reset saw a plain sign-in form and no word that it worked. */}
          {!error && typeof location.state?.message === 'string' && location.state.message && (
            <div data-testid="doorlypro-login-notice" role="status"
              className="mb-5 px-4 py-3 rounded-xl bg-doorly-teal-light text-black text-sm">
              {location.state.message}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                autoComplete="email"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = '#3a6ad9')}
                onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
              />
            </div>

            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                placeholder="Your password"
                autoComplete="current-password"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = '#3a6ad9')}
                onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
              />
            </div>

            <div className="text-right mb-6">
              <Link to="/doorlypro/forgot-password" className="text-sm" style={{ color: '#3a6ad9' }}>
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary btn-full text-white hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: '#3a6ad9' }}
            >
              {loading ? 'Signing In...' : (
                <>
                  <span>Sign In</span>
                  <span className="btn-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
                  </span>
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Don't have an account?{' '}
          <Link to="/doorlypro/start" className="font-semibold" style={{ color: '#3a6ad9' }}>
            Apply to become a partner
          </Link>
        </p>
      </div>
    </div>
  );
}
